import React, { useEffect, useState } from "react";
import AppAccountSection from "./AppAccountSection";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    Chip,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Avatar,
    IconButton,
    CircularProgress,
    Alert,
    List,
    ListItem,
    ListItemText,
} from "@mui/material";
import {
    Close as CloseIcon,
    Person as PersonIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    LocationOn as LocationIcon,
    ShoppingCart as OrderIcon,
    Security as WarrantyIcon,
    LocalHospital as InsuranceIcon,
    TrendingUp as StatsIcon,
    Delete as DeleteIcon,
    WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";

const CustomerMasterDetailsDialog = ({
    open,
    onClose,
    customerMasterData,
    loading = false,
    error = null,
    onFetchDeletionImpact,
    onDeleteCustomer,
    deletingCustomer = false,
}) => {
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleteImpactLoading, setDeleteImpactLoading] = useState(false);
    const [deleteImpactData, setDeleteImpactData] = useState(null);
    const [deleteError, setDeleteError] = useState(null);

    useEffect(() => {
        if (!open) {
            setDeleteConfirmOpen(false);
            setDeleteImpactLoading(false);
            setDeleteImpactData(null);
            setDeleteError(null);
        }
    }, [open]);

    if (!open) return null;

    const getStatusColor = (status) => {
        switch (status) {
            case "paid":
                return "success";
            case "pending":
                return "warning";
            case "partial":
                return "info";
            case "failed":
                return "error";
            case "cancelled":
                return "error";
            default:
                return "default";
        }
    };

    const getCreationSourceColor = (source) => {
        switch (source) {
            case "staff_created":
                return "primary";
            case "warranty_registration":
                return "success";
            case "insurance_renewal":
                return "warning";
            default:
                return "default";
        }
    };

    const getCreationSourceLabel = (source) => {
        switch (source) {
            case "staff_created":
                return "Staff Created";
            case "warranty_registration":
                return "Warranty Registration";
            case "insurance_renewal":
                return "Insurance Renewal";
            default:
                return "Unknown";
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
        }).format(amount);
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString("en-IN", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    if (loading) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography variant="h6">Customer Details</Typography>
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress />
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    if (error) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography variant="h6">Customer Details</Typography>
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="error">{error}</Alert>
                </DialogContent>
            </Dialog>
        );
    }

    if (!customerMasterData) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography variant="h6">Customer Details</Typography>
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="info">No customer data available</Alert>
                </DialogContent>
            </Dialog>
        );
    }

    const { customer, creation_source, orders, warranty_registrations, insurance_renewals } = customerMasterData;

    const getFallbackDeletionImpact = () => ({
        customer: {
            id: customer?.id,
            name: customer?.name,
            mobile: customer?.mobile,
            email: customer?.email,
        },
        impact: {
            customer: 1,
            orders: orders?.data?.length || 0,
            warranty_registrations: warranty_registrations?.data?.length || 0,
            insurance_renewals: insurance_renewals?.data?.length || 0,
        },
        backup_notice: "A backup snapshot will be stored before permanent deletion.",
    });

    const handleOpenDeleteConfirm = async () => {
        setDeleteConfirmOpen(true);
        setDeleteError(null);
        setDeleteImpactLoading(true);

        try {
            if (customer?.id && onFetchDeletionImpact) {
                const impactResult = await onFetchDeletionImpact(customer.id);
                setDeleteImpactData(impactResult || getFallbackDeletionImpact());
            } else {
                setDeleteImpactData(getFallbackDeletionImpact());
            }
        } catch (impactError) {
            setDeleteError(impactError?.data?.message || impactError?.message || "Failed to load delete impact.");
            setDeleteImpactData(getFallbackDeletionImpact());
        } finally {
            setDeleteImpactLoading(false);
        }
    };

    const handleConfirmDelete = async () => {
        try {
            if (customer?.id && onDeleteCustomer) {
                await onDeleteCustomer(customer.id);
            }
            setDeleteConfirmOpen(false);
        } catch (deleteActionError) {
            const errorMessage =
                deleteActionError?.data?.message || deleteActionError?.message || "Failed to delete customer.";
            setDeleteError(errorMessage);
            alert(errorMessage);
        }
    };

    const impactToDisplay = deleteImpactData || getFallbackDeletionImpact();

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Avatar sx={{ width: 48, height: 48, bgcolor: "primary.main" }}>
                            <PersonIcon />
                        </Avatar>
                        <Box>
                            <Typography variant="h6">{customer.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Customer ID: {customer.id}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Grid container spacing={3}>
                    {/* Customer Information */}
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <PersonIcon color="primary" />
                                    Customer Information
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Contact Information
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                        <PhoneIcon fontSize="small" color="action" />
                                        <Typography variant="body2">{customer.mobile}</Typography>
                                    </Box>
                                    {customer.email && (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <EmailIcon fontSize="small" color="action" />
                                            <Typography variant="body2">{customer.email}</Typography>
                                        </Box>
                                    )}
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Address
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                        <LocationIcon fontSize="small" color="action" sx={{ mt: 0.5 }} />
                                        <Box>
                                            <Typography variant="body2">{customer.address}</Typography>
                                            <Typography variant="body2">
                                                {customer.city}, {customer.state} - {customer.pincode}
                                            </Typography>
                                            {customer.landmark && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Landmark: {customer.landmark}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Status & Creation
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                                        <Chip
                                            label={customer.is_active ? "Active" : "Inactive"}
                                            color={customer.is_active ? "success" : "error"}
                                            size="small"
                                        />
                                        <Chip
                                            label={getCreationSourceLabel(creation_source.source)}
                                            color={getCreationSourceColor(creation_source.source)}
                                            size="small"
                                        />
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Created: {formatDate(customer.created_at)}
                                    </Typography>
                                </Box>

                                <AppAccountSection key={customer.id} appAccount={customer.app_account} />

                                {customer.notes && (
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Notes
                                        </Typography>
                                        <Typography variant="body2">{customer.notes}</Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Statistics Summary */}
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <StatsIcon color="primary" />
                                    Summary Statistics
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Box sx={{ textAlign: "center" }}>
                                            <Typography variant="h4" color="primary">
                                                {orders.stats.total_orders}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Total Orders
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ textAlign: "center" }}>
                                            <Typography variant="h4" color="success.main">
                                                {formatCurrency(orders.stats.total_amount)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Total Amount
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ textAlign: "center" }}>
                                            <Typography variant="h4" color="warning.main">
                                                {warranty_registrations.stats.total_registrations}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Warranty Registrations
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ textAlign: "center" }}>
                                            <Typography variant="h4" color="info.main">
                                                {insurance_renewals.stats.total_renewals}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Insurance Renewals
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Orders */}
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <OrderIcon color="primary" />
                                    Orders ({orders.data.length})
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                
                                {orders.data.length > 0 ? (
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Order #</TableCell>
                                                    <TableCell>Product</TableCell>
                                                    <TableCell>Amount</TableCell>
                                                    <TableCell>Payment Status</TableCell>
                                                    <TableCell>Payment Type</TableCell>
                                                    <TableCell>Created By</TableCell>
                                                    <TableCell>Date</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {orders.data.map((order) => (
                                                    <TableRow key={order.id}>
                                                        <TableCell>{order.order_number}</TableCell>
                                                        <TableCell>{order.product?.name || order.product_name}</TableCell>
                                                        <TableCell>{formatCurrency(order.total_amount)}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={order.payment_status}
                                                                color={getStatusColor(order.payment_status)}
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                        <TableCell>{order.payment_type}</TableCell>
                                                        <TableCell>{order.staff?.name || "N/A"}</TableCell>
                                                        <TableCell>{formatDate(order.created_at)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No orders found
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Warranty Registrations */}
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <WarrantyIcon color="primary" />
                                    Warranty Registrations ({warranty_registrations.data.length})
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                
                                {warranty_registrations.data.length > 0 ? (
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Registration #</TableCell>
                                                    <TableCell>Product</TableCell>
                                                    <TableCell>Purchase Date</TableCell>
                                                    <TableCell>Dealer</TableCell>
                                                    <TableCell>Location</TableCell>
                                                    <TableCell>Date</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {warranty_registrations.data.map((warranty) => (
                                                    <TableRow key={warranty.id}>
                                                        <TableCell>{warranty.registration_number}</TableCell>
                                                        <TableCell>{warranty.product_name}</TableCell>
                                                        <TableCell>{formatDate(warranty.purchase_date)}</TableCell>
                                                        <TableCell>{warranty.dealer_name}</TableCell>
                                                        <TableCell>{warranty.city}, {warranty.state}</TableCell>
                                                        <TableCell>{formatDate(warranty.created_at)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No warranty registrations found
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Insurance Renewals */}
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <InsuranceIcon color="primary" />
                                    Insurance Renewals ({insurance_renewals.data.length})
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                
                                {insurance_renewals.data.length > 0 ? (
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Insurance #</TableCell>
                                                    <TableCell>Product</TableCell>
                                                    <TableCell>Premium</TableCell>
                                                    <TableCell>Payment Status</TableCell>
                                                    <TableCell>Validity</TableCell>
                                                    <TableCell>Date</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {insurance_renewals.data.map((insurance) => (
                                                    <TableRow key={insurance.id}>
                                                        <TableCell>{insurance.insurance_number}</TableCell>
                                                        <TableCell>{insurance.product_name}</TableCell>
                                                        <TableCell>{formatCurrency(insurance.premium_amount)}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={insurance.payment_status}
                                                                color={getStatusColor(insurance.payment_status)}
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {formatDate(insurance.validity_from)} - {formatDate(insurance.validity_to)}
                                                        </TableCell>
                                                        <TableCell>{formatDate(insurance.created_at)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No insurance renewals found
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={handleOpenDeleteConfirm}
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    disabled={deletingCustomer}
                >
                    Delete Customer
                </Button>
                <Button onClick={onClose} variant="outlined">
                    Close
                </Button>
            </DialogActions>
            </Dialog>

            <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <WarningAmberIcon color="warning" />
                    Confirm Customer Deletion
                </DialogTitle>
                <DialogContent>
                    {deleteError && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {deleteError}
                        </Alert>
                    )}
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        This will permanently delete these records:
                    </Typography>
                    {deleteImpactLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <List dense>
                            <ListItem>
                                <ListItemText primary="Customer records" secondary={impactToDisplay?.impact?.customer || 1} />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Order records" secondary={impactToDisplay?.impact?.orders || 0} />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="Warranty records"
                                    secondary={impactToDisplay?.impact?.warranty_registrations || 0}
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="Insurance records"
                                    secondary={impactToDisplay?.impact?.insurance_renewals || 0}
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="Lucky draw records"
                                    secondary={impactToDisplay?.impact?.lucky_draw_entries || 0}
                                />
                            </ListItem>
                        </List>
                    )}
                    <Alert severity="info" sx={{ mt: 1 }}>
                        {impactToDisplay?.backup_notice || "A backup snapshot will be stored before deletion."}
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)} variant="outlined" disabled={deletingCustomer}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        disabled={deleteImpactLoading || deletingCustomer}
                    >
                        {deletingCustomer ? "Deleting..." : "Confirm Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CustomerMasterDetailsDialog;
